import { createApiCall, paginatedApiCall } from "services/api/createApiCall";
import { DataWithPagination } from "services/api/types";
import { Pagination } from "services/search";
import {
  OverviewTotalVehiclesResponse,
  OverviewMonthlyExpiringResponse,
  OverviewRevenueExpenseResponse,
  OverviewOffersResponse,
  OverviewInvoicesResponse,
  OverviewInquiriesResponse,
  FuelDistributionItem,
  MarginStatsResponse,
  PriceCompareItem,
  BrandStatsResponse,
  VehicleAlertRow,
  VehicleAlertsSummary,
  VehicleProfitabilityRow,
} from "./types";
import {
  transformOverviewTotalVehicles,
  transformOverviewMonthlyExpiring,
  transformOverviewRevenueExpense,
  transformOverviewOffers,
  transformOverviewInvoices,
  transformOverviewInquiries,
  transformVehicleAlertRow,
  transformVehicleProfitabilityRow,
} from "./transformations";
import { buildRangeParams } from "./range";

/*---------------------------------------------------------------------------------------
  ALERTS (Upozorenja) — jedinstveni endpoint (zamjena za N+1 agregaciju u browseru)
-----------------------------------------------------------------------------------------*/

export interface VehicleAlertsFilters {
  search?: string | null;
  type?: string | null;
  status?: string | null;
  expirationDateFrom?: Date | null;
  expirationDateTo?: Date | null;
}

export const getVehicleAlertsAPI = async (
  pagination: Pagination,
  filters: VehicleAlertsFilters
): Promise<DataWithPagination<VehicleAlertRow>> => {
  let path = `/v1/dashboard/alerts?`;

  if (filters.search) path += `&search=${encodeURIComponent(filters.search)}`;
  if (filters.type) path += `&type=${filters.type}`;
  if (filters.status) path += `&status=${filters.status}`;
  if (filters.expirationDateFrom)
    path += `&expirationDateFrom=${filters.expirationDateFrom.toISOString()}`;
  if (filters.expirationDateTo)
    path += `&expirationDateTo=${filters.expirationDateTo.toISOString()}`;

  return await paginatedApiCall(path, pagination, transformVehicleAlertRow);
};

export const getVehicleAlertsSummaryAPI = async (): Promise<VehicleAlertsSummary> => {
  const response = await createApiCall(
    { path: "/v1/dashboard/alerts/summary", method: "GET" },
    { getMessage: { error: (error: any) => `${error.response.data.message}` } }
  )();
  return {
    total: response.data?.total ?? 0,
    expired: response.data?.expired ?? 0,
    expiring: response.data?.expiring ?? 0,
    byType: (response.data?.byType ?? []).map((b: any) => ({
      type: b.type,
      expired: b.expired ?? 0,
      expiring: b.expiring ?? 0,
      total: b.total ?? 0,
    })),
  };
};

/*---------------------------------------------------------------------------------------
  FINANCIJE — profitabilnost po vozilu
-----------------------------------------------------------------------------------------*/

export const getVehicleProfitabilityAPI = async (
  from?: Date | null,
  to?: Date | null
): Promise<VehicleProfitabilityRow[]> => {
  const response = await createApiCall(
    { path: `/v1/dashboard/vehicle-profitability${buildRangeParams(from, to)}`, method: "GET" },
    { getMessage: { error: (error: any) => `${error.response.data.message}` } }
  )();
  return (response.data ?? []).map(transformVehicleProfitabilityRow);
};

/*---------------------------------------------------------------------------------------
  VEHICLE STATS — zasebni endpointi za svaki slide
-----------------------------------------------------------------------------------------*/

/** Nova vs rabljena vozila */
export const getVehicleStatsNewUsedAPI = async (): Promise<{ total: number; newVehicles: number; usedVehicles: number }> => {
  const response = await createApiCall(
    { path: "/v1/dashboard/vehicle-stats/new-used", method: "GET" },
    { getMessage: { error: (error: any) => `${error.response.data.message}` } }
  )();
  return {
    total: response.data.total ?? 0,
    newVehicles: response.data.newVehicles ?? 0,
    usedVehicles: response.data.usedVehicles ?? 0,
  };
};

/** Distribucija po tipu goriva */
export const getVehicleStatsFuelAPI = async (): Promise<FuelDistributionItem[]> => {
  const response = await createApiCall(
    { path: "/v1/dashboard/vehicle-stats/fuel-distribution", method: "GET" },
    { getMessage: { error: (error: any) => `${error.response.data.message}` } }
  )();
  return (response.data ?? []).map((r: any) => ({ name: r.name ?? "Ostalo", count: r.count ?? 0 }));
};

/** Ukupna marža po mjesecu — zadnjih 7 mjeseci */
export const getVehicleStatsMarginAPI = async (from?: Date | null, to?: Date | null): Promise<MarginStatsResponse> => {
  const response = await createApiCall(
    { path: `/v1/dashboard/vehicle-stats/margin${buildRangeParams(from, to)}`, method: "GET" },
    { getMessage: { error: (error: any) => `${error.response.data.message}` } }
  )();
  return {
    totalMargin: response.data.totalMargin ?? 0,
    avgMarginPercent: response.data.avgMarginPercent ?? 0,
    changePercent: response.data.changePercent ?? 0,
    months: (response.data.months ?? []).map((m: any) => ({
      month: m.month ?? "",
      monthLabel: m.monthLabel ?? "",
      start: m.start ?? null,
      end: m.end ?? null,
      totalMargin: m.totalMargin ?? 0,
      totalSellingPrice: m.totalSellingPrice ?? 0,
      totalPurchasePrice: m.totalPurchasePrice ?? 0,
      count: m.count ?? 0,
      isCurrent: m.isCurrent ?? false,
    })),
  };
};

/** Nabavna vs prodajna cijena — top 10 */
export const getVehicleStatsPriceCompareAPI = async (from?: Date | null, to?: Date | null): Promise<PriceCompareItem[]> => {
  const response = await createApiCall(
    { path: `/v1/dashboard/vehicle-stats/price-compare${buildRangeParams(from, to)}`, method: "GET" },
    { getMessage: { error: (error: any) => `${error.response.data.message}` } }
  )();
  return (response.data ?? []).map((r: any) => ({
    id: r.id ?? "",
    name: r.name ?? "",
    totalPurchasePrice: r.totalPurchasePrice ?? 0,
    sellingPrice: r.sellingPrice ?? 0,
  }));
};

/** Vozila po brandu — grupirano po proizvođaču s modelima */
export const getVehicleStatsBrandsAPI = async (): Promise<BrandStatsResponse> => {
  const response = await createApiCall(
    { path: "/v1/dashboard/vehicle-stats/brands", method: "GET" },
    { getMessage: { error: (error: any) => `${error.response.data.message}` } }
  )();
  return {
    total: response.data?.total ?? 0,
    brands: (response.data?.brands ?? []).map((b: any) => ({
      id: b.id ?? null,
      brand: b.brand ?? "",
      image: b.image ?? "",
      total: b.total ?? 0,
      models: (b.models ?? []).map((m: any) => ({ model: m.model ?? "", count: m.count ?? 0 })),
    })),
  };
};

/*---------------------------------------------------------------------------------------
  OVERVIEW ENDPOINTS
-----------------------------------------------------------------------------------------*/

export const getOverviewTotalVehiclesAPI = async (): Promise<OverviewTotalVehiclesResponse> => {
  const response = await createApiCall(
    { path: "/v1/dashboard/overview/total-vehicles", method: "GET" },
    { getMessage: { error: (error: any) => `${error.response.data.message}` } }
  )();
  return transformOverviewTotalVehicles(response.data);
};

export const getOverviewRegistrationExpiringAPI = async (): Promise<OverviewMonthlyExpiringResponse> => {
  const response = await createApiCall(
    { path: "/v1/dashboard/overview/registration-expiring", method: "GET" },
    { getMessage: { error: (error: any) => `${error.response.data.message}` } }
  )();
  return transformOverviewMonthlyExpiring(response.data);
};

export const getOverviewInsuranceExpiringAPI = async (): Promise<OverviewMonthlyExpiringResponse> => {
  const response = await createApiCall(
    { path: "/v1/dashboard/overview/insurance-expiring", method: "GET" },
    { getMessage: { error: (error: any) => `${error.response.data.message}` } }
  )();
  return transformOverviewMonthlyExpiring(response.data);
};

export const getOverviewCascoExpiringAPI = async (): Promise<OverviewMonthlyExpiringResponse> => {
  const response = await createApiCall(
    { path: "/v1/dashboard/overview/casco-expiring", method: "GET" },
    { getMessage: { error: (error: any) => `${error.response.data.message}` } }
  )();
  return transformOverviewMonthlyExpiring(response.data);
};

export const getOverviewFireExtinguisherExpiringAPI = async (): Promise<OverviewMonthlyExpiringResponse> => {
  const response = await createApiCall(
    { path: "/v1/dashboard/overview/fire-extinguisher-expiring", method: "GET" },
    { getMessage: { error: (error: any) => `${error.response.data.message}` } }
  )();
  return transformOverviewMonthlyExpiring(response.data);
};

export const getOverviewRevenueExpenseAPI = async (from?: Date | null, to?: Date | null): Promise<OverviewRevenueExpenseResponse> => {
  const response = await createApiCall(
    { path: `/v1/dashboard/overview/revenue-expense${buildRangeParams(from, to)}`, method: "GET" },
    { getMessage: { error: (error: any) => `${error.response.data.message}` } }
  )();
  return transformOverviewRevenueExpense(response.data);
};

export const getOverviewOffersAPI = async (from?: Date | null, to?: Date | null): Promise<OverviewOffersResponse> => {
  const response = await createApiCall(
    { path: `/v1/dashboard/overview/offers${buildRangeParams(from, to)}`, method: "GET" },
    { getMessage: { error: (error: any) => `${error.response.data.message}` } }
  )();
  return transformOverviewOffers(response.data);
};

export const getOverviewInvoicesAPI = async (from?: Date | null, to?: Date | null): Promise<OverviewInvoicesResponse> => {
  const response = await createApiCall(
    { path: `/v1/dashboard/overview/invoices${buildRangeParams(from, to)}`, method: "GET" },
    { getMessage: { error: (error: any) => `${error.response.data.message}` } }
  )();
  return transformOverviewInvoices(response.data);
};

export const getOverviewInquiriesAPI = async (): Promise<OverviewInquiriesResponse> => {
  const response = await createApiCall(
    { path: "/v1/dashboard/overview/inquiries", method: "GET" },
    { getMessage: { error: (error: any) => `${error.response.data.message}` } }
  )();
  return transformOverviewInquiries(response.data);
};
